import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { lastDays } from "./networkFmt";
import NetworkPicker from "./NetworkPicker";
import NetworkHeadline from "./NetworkHeadline";
import NetworkSpendChart from "./NetworkSpendChart";

/** Home dashboard: AI spend + reuse of all servers, one channel or one server of the Radar network, for the last `days`. */
export default function NetworkOverview({ days }) {
    const [access, setAccess] = useState(null);
    const [sel, setSel] = useState({ scope: "all", channel: null, server: null });
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/admin/billing/network/settings").then((r) => setAccess(r.data)).catch(() => setAccess({ can_view: false }));
    }, []);

    useEffect(() => {
        if (!access?.can_view) return;
        const { from, to } = lastDays(days);
        const p = new URLSearchParams({ scope: sel.scope, date_from: from, date_to: to });
        if (sel.scope === "channel") p.set("channel", sel.channel);
        if (sel.scope === "server") p.set("server", sel.server);
        api.get(`/admin/billing/network/summary?${p.toString()}`)
            .then((r) => { setData(r.data); setError(null); })
            .catch((e) => setError(formatApiErrorDetail(e)));
    }, [access, sel, days]);

    if (!access?.can_view) return null;
    return (
        <section className="space-y-4 mb-10 nc-fade-in" data-testid="dash-network">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Globe2 className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                    <div>
                        <div className="label-xs text-[#7C3AED]">Radar network</div>
                        <div className="font-display text-xl tracking-tight">AI costs across servers</div>
                    </div>
                </div>
                <Link to="/billing" className="text-[11px] font-mono text-[#7C3AED] hover:text-[#a17bff]" data-testid="dash-network-open-billing">
                    Breakdowns, calls &amp; CSV in Billing →
                </Link>
            </div>
            <NetworkPicker sel={sel} servers={data?.by_server || []} onChange={(patch) => setSel((s) => ({ ...s, ...patch }))} />
            {error && <div className="border border-red-500/30 bg-red-500/5 text-red-300 text-xs rounded-md p-3" data-testid="dash-network-error">{error}</div>}
            {!data && !error && <div className="text-white/40 text-xs" data-testid="dash-network-loading">Loading network costs…</div>}
            {data && (
                <>
                    <NetworkHeadline data={data} showNote={false} />
                    <NetworkSpendChart data={data} />
                    <p className="text-[11px] text-white/35" data-testid="dash-network-note">
                        Every AI call and reuse on each server, shared through Radar Sync (a server's figures can lag a few minutes) — so it can differ
                        slightly from this server's own tiles below, which count agent and flow runs only.
                    </p>
                </>
            )}
        </section>
    );
}
